import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from '../additional-providers-form/additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton } from '../../../base';
import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Field, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { InputField } from '../../../base/form';

interface ProviderFormValues {
    providerLookup?: string;
    providerId?: string;
    first: string;
    last: string;
    npi: string;
}

const ProviderSchema = Yup.object().shape({
    first: Yup.string().required('First name is required'),
    last: Yup.string().required('Last name is required'),
    npi: Yup.string().required('NPI is required').max(10, 'NPI must be 10 characters or less'),
});

const initialValues: ProviderFormValues = {
    providerLookup: '',
    providerId: '',
    first: '',
    last: '',
    npi: '',
};

interface Props {
    claimEditorStore: ClaimEditorStore;
    serviceIndex: number;
}

export const ServiceLineProvidersForm: React.FunctionComponent<Props> = observer(({ claimEditorStore, serviceIndex }) => {
    const [showRendering, setShowRendering] = useState(false);
    const [showReferring, setShowReferring] = useState(false);
    const [showSupervising, setShowSupervising] = useState(false);

    useEffect(() => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers: any[] = svc.providers || [];
        const types = providers.map(p => String(p?.type).toUpperCase());
        setShowRendering(types.includes('RENDERING'));
        setShowReferring(types.includes('REFERRING'));
        setShowSupervising(types.includes('SUPERVISING'));
    }, [claimEditorStore.services, serviceIndex]);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'RENDERING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: 'RENDERING' as any,
                        } as any);
                        setShowRendering(true);
                    }}
                    disabled={showRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'REFERRING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: 'REFERRING' as any,
                        } as any);
                        setShowReferring(true);
                    }}
                    disabled={showReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton
                    variant="primary"
                    onClick={() => {
                        claimEditorStore.updateServiceLineProvider(serviceIndex, 'SUPERVISING', {
                            first: '',
                            last: '',
                            npi: '',
                            type: 'SUPERVISING' as any,
                        } as any);
                        setShowSupervising(true);
                    }}
                    disabled={showSupervising}
                >
                    + Add Supervising
                </BaseButton>
            </CS.ButtonContainer>

            {showRendering && (
                <ProviderBlock
                    title="Rendering"
                    type={CreateClaimProviderType.RENDERING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'RENDERING'); setShowRendering(false); }}
                />
            )}
            {showReferring && (
                <ProviderBlock
                    title="Referring"
                    type={CreateClaimProviderType.REFERRING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'REFERRING'); setShowReferring(false); }}
                />
            )}
            {showSupervising && (
                <ProviderBlock
                    title="Supervising"
                    type={CreateClaimProviderType.SUPERVISING}
                    serviceIndex={serviceIndex}
                    claimEditorStore={claimEditorStore}
                    onDelete={() => { claimEditorStore.deleteServiceLineProvider(serviceIndex, 'SUPERVISING'); setShowSupervising(false); }}
                />
            )}
        </S.FormContainer>
    );
});

const ProviderBlock: React.FunctionComponent<{
    title: string;
    type: CreateClaimProviderType;
    serviceIndex: number;
    claimEditorStore: ClaimEditorStore;
    onDelete: () => void;
}> = observer(({ title, type, serviceIndex, claimEditorStore, onDelete }) => {
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const [providerOptions, setProviderOptions] = useState<Array<{ label: string; value: string; provider: SupportProvider }>>([]);
    const [isLookupOpen, setIsLookupOpen] = useState(false);
    const [isLookupLoading, setIsLookupLoading] = useState(false);
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Normalize enum or string to canonical uppercase string key
    const typeString = React.useMemo(() => {
        const maybeString = typeof type === 'string' ? type : (CreateClaimProviderType as any)[type];
        return String(maybeString).toUpperCase();
    }, [type]);

    const getInitialValues = useCallback((): ProviderFormValues => {
        const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
        const providers: any[] = Array.isArray(svc.providers)
            ? svc.providers
            : svc.providers && typeof svc.providers === 'object'
                ? Object.values(svc.providers)
                : [];
        const existingProvider = providers.find((p: any) => String(p?.type).toUpperCase() === typeString) as CreateClaimProvider | undefined;
        
        if (existingProvider) {
            return {
                providerLookup: [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') || '',
                providerId: (existingProvider as any).providerId || (existingProvider as any).id || '',
                first: existingProvider.first || '',
                last: existingProvider.last || '',
                npi: existingProvider.npi || '',
            };
        }
        return initialValues;
    }, [claimEditorStore.services, serviceIndex, title]);

    const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
        return {
            providerId: values.providerId || undefined,
            first: values.first,
            last: values.last,
            npi: values.npi,
            type: typeString as any,
        };
    };

    const debouncedSave = useCallback(
        (values: ProviderFormValues) => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }

            saveTimerRef.current = setTimeout(() => {
                const providerData = convertToProvider(values);
                claimEditorStore.updateServiceLineProvider(serviceIndex, typeString as any, providerData);
            }, 500);
        },
        [claimEditorStore, serviceIndex, typeString],
    );


        useEffect(() => {
            return () => {
                if (saveTimerRef.current) {
                    clearTimeout(saveTimerRef.current);
                }
                if (lookupDebounceRef.current) {
                    clearTimeout(lookupDebounceRef.current);
                }
            };
        }, []);

    return (
        <CS.SectionContainer>
            <CS.SectionHeader>
                <CS.SectionTitle>{title}</CS.SectionTitle>
                <BaseButton variant="warning" onClick={onDelete}>✕ Delete {title} Provider</BaseButton>
            </CS.SectionHeader>

            <Formik
                initialValues={getInitialValues()}
                validationSchema={ProviderSchema}
                onSubmit={() => {
                    // No-op, we auto-save
                }}
                enableReinitialize
                validateOnMount={false}
                validateOnChange={true}
                validateOnBlur={false}
            >
                {formik => {
                    // Auto-save on value change - save whenever values change, not just when dirty
                    useEffect(() => {
                        debouncedSave(formik.values);
                    }, [formik.values, debouncedSave]);

                    return (
                        <>
                            <S.FormGrid>
                                <S.FormField span={3}>
                                    <S.Label>{title} Provider Lookup</S.Label>
                                    <Field
                                        name="providerLookup"
                                        component={AsyncSelectField}
                                        placeholder="Type last name to search"
                                        loadOptions={async (q: string) => {
                                            const results = await claimEditorStore.searchProvidersByLastName(q, typeString as any);
                                            return results.map(p => ({ 
                                                label: [p.first, p.last].filter(Boolean).join(' ') || p.organization || '', 
                                                value: p.id as any, 
                                                provider: p,
                                                info: p.npi ? ' | ' + p.npi : undefined,
                                            } as any));
                                        }}
                                        onChange={(opt: any, form: any) => {
                                            const p = opt?.provider as SupportProvider;
                                            if (!p) {
                                                form.setFieldValue('providerId', undefined);
                                                return;
                                            }
                                            const newValues: ProviderFormValues = {
                                                providerLookup: [p.first, p.last].filter(Boolean).join(' ') || p.organization || '',
                                                providerId: p.id || '',
                                                first: p.first || '',
                                                last: p.last || '',
                                                npi: p.npi || '',
                                            };
                                            form.setValues(newValues);
                                            debouncedSave(newValues);
                                        }}
                                    />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label required>First Name</S.Label>
                                    <Field name="first" placeholder="First Name" component={InputField} />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label required>Last Name</S.Label>
                                    <Field name="last" placeholder="Last Name" component={InputField} />
                                </S.FormField>

                                <S.FormField>
                                    <S.Label required>NPI</S.Label>
                                    <Field name="npi" placeholder="NPI" component={InputField} maxLength={10 as any} />
                                </S.FormField>
                            </S.FormGrid>
                        </>
                    );
                }}
            </Formik>
        </CS.SectionContainer>
    );
});

export default ServiceLineProvidersForm;

